import { useQuery } from '@tanstack/react-query';
import {
  asContentArray,
  asContentObject,
  fetchContentBlocks,
  getContentValue,
} from '../lib/content';

function ProjectCard({ project, index }) {
  const projectContent = asContentObject(project, {});
  const stats = asContentArray(projectContent.stats).slice(0, 3);
  const tags = asContentArray(projectContent.tags);

  return (
    <article className="surface-card p-8 md:p-10">
      <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
        <div className="flex-1">
          <p className="eyebrow">
            {projectContent.id || `Project ${index + 1}`}
            {projectContent.category ? ` / ${projectContent.category}` : ''}
          </p>
          <h2 className="mt-5 font-display text-5xl leading-none tracking-tight text-slate-900">
            {projectContent.name || projectContent.title || 'Untitled project'}
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-8 text-slate-600">
            {projectContent.description || 'Add a project description from the admin content block.'}
          </p>
        </div>

        {projectContent.status && <span className="meta-pill self-start">{projectContent.status}</span>}
      </div>

      {stats.length > 0 && (
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {stats.map((stat, statIndex) => {
            const statContent = asContentObject(stat, {});

            return (
              <div key={statContent.label || statIndex} className="rounded-[22px] border border-slate-100 bg-slate-50/80 px-5 py-5">
                <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                  {statContent.label || 'Metric'}
                </p>
                <p className="mt-4 font-display text-4xl leading-none text-slate-900">
                  {statContent.value || '-'}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {tags.length > 0 && (
        <div className="section-divider mt-8 pt-6">
          <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Technology</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {tags.map((item, tagIndex) => (
              <span key={`${item}-${tagIndex}`} className="meta-pill">
                {item}
              </span>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function Products() {
  const { data: contentBlocks = [] } = useQuery({
    queryKey: ['siteContent'],
    queryFn: fetchContentBlocks,
  });

  const workValue = getContentValue(contentBlocks, ['work', 'products'], {});
  const workContent = asContentObject(workValue, {});
  const projects = asContentArray(
    workContent.projects,
    Array.isArray(workValue) ? workValue : [],
  );

  return (
    <main className="page-shell">
      <div className="page-width">
        <section className="page-header">
          <p className="eyebrow">{workContent.eyebrow || 'Selected work'}</p>
          <h1 className="section-title mt-6">
            {workContent.title || 'Selected work'}
          </h1>
          <p className="section-subtitle max-w-2xl">
            {workContent.subtitle || 'Only published project data appears on this page.'}
          </p>
        </section>

        <div className="mt-16 space-y-6">
          {projects.length > 0 ? (
            projects.map((project, index) => <ProjectCard key={project.id || project.name || index} project={project} index={index} />)
          ) : (
            <div className="surface-card p-8 text-sm leading-8 text-slate-600 md:p-10">
              No selected work has been published yet.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
